import React, { useState } from 'react'
import {
  Table,
  TableHeaderItem,
  Flex,
  Pill,
  Box,
  Tooltip,
} from '@sketch/components'
import {
  DocumentPreviewsLayoutWrapper,
  TableCell,
  TableRow,
  DocumentThumbnail,
  DocumentName,
  LockIcon,
  ArrowIcon,
  SlidersIcon,
  LinkIcon,
  PreviewOverview,
  ActionButton,
} from './DocumentPreviewsLayout.styles'

type SortOrder = 'ASC' | 'DESC'

export interface DocumentPreview {
  uuid: string
  name: string
  previews: {
    uuid: string
    name: string
    passwordProtected: boolean
    expiryDate: string | null
  }[]
}

interface PreviewRowProps {
  document: DocumentPreview
  onCopyLink: (documentUUID: string, previewUUID: string) => void
  onPreviewSettings: (documentUUID: string, previewUUID: string) => void
  onOpenPreview: (documentUUID: string, previewUUID: string) => void
}

const isExpired = (date: string | null) => {
  if (!date) {
    return false
  }

  const parsedDate = new Date(date)
  const now = new Date()

  return parsedDate < now
}

export const PreviewRow = ({
  document,
  onCopyLink,
  onOpenPreview,
  onPreviewSettings,
}: PreviewRowProps) => (
  <TableRow key={`document-${document.uuid}`}>
    <TableCell>
      <Flex alignItems="center">
        <DocumentThumbnail />
        <DocumentName>{document.name}</DocumentName>
      </Flex>
    </TableCell>
    <TableCell>
      {document.previews.map(preview => (
        <Flex
          width={350}
          height={45}
          mt={1}
          direction="column"
          justifyContent="space-between"
          key={`preview-${preview.uuid}`}
        >
          <Box>
            <PreviewOverview>
              {preview.passwordProtected ? (
                <Tooltip content="Pasword Protected" placement="top">
                  <LockIcon />
                </Tooltip>
              ) : null}
              {preview.name}
              {isExpired(preview.expiryDate) ? (
                <Pill variant="secondary">Expired</Pill>
              ) : null}
            </PreviewOverview>
          </Box>
          <Box>
            <Flex alignItems="center">
              <Tooltip content="Copy Link" placement="top">
                <ActionButton
                  onClick={() => onCopyLink(document.uuid, preview.uuid)}
                >
                  <LinkIcon />
                </ActionButton>
              </Tooltip>
              <Tooltip content="Edit Preview" placement="top">
                <ActionButton
                  onClick={() => onPreviewSettings(document.uuid, preview.uuid)}
                >
                  <SlidersIcon />
                </ActionButton>
              </Tooltip>
              <Tooltip content="Open Preview" placement="top">
                <ActionButton
                  onClick={() => onOpenPreview(document.uuid, preview.uuid)}
                >
                  <ArrowIcon />
                </ActionButton>
              </Tooltip>
            </Flex>
          </Box>
        </Flex>
      ))}
    </TableCell>
  </TableRow>
)

export interface DocumentPreviewsLayoutProps {
  items: DocumentPreview[]
  header: TableHeaderItem[]
  renderItem: (item: DocumentPreview) => React.ReactNode
}

export const DocumentPreviewsLayout: React.FC<DocumentPreviewsLayoutProps> = ({
  items,
  header,
  renderItem,
}) => {
  const [sortOrder, setSortOrder] = useState<SortOrder>('DESC')

  const sortedItems = items.sort((a, b) =>
    sortOrder === 'ASC'
      ? a.name.localeCompare(b.name)
      : b.name.localeCompare(a.name)
  )

  return (
    <DocumentPreviewsLayoutWrapper>
      <Table
        header={header}
        items={sortedItems}
        renderItemKey={item => item.uuid}
        renderItem={renderItem}
        sortable
        sortDirection={sortOrder}
        onHeaderClick={() => {
          setSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'))
        }}
      />
    </DocumentPreviewsLayoutWrapper>
  )
}
